import { calculatingPageCount, cn } from '@/lib/utils'
import { useTable } from '@/hooks/use-table'
import { Table } from '@/components/data-table/table'
import { UserTableColumn } from './users-table-columns'
import { getOrganizationUsersOrganizationOrganizationIdUsersGetOptions } from '@/generated/@tanstack/react-query.gen'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates,
} from 'nuqs'
import { UserTab } from './users-tab'
import SearchAutocompleted from './search-autocompleted'
import UserSorting from './users-sorting'
import { useUser } from '@/pages/enterprise/user-provider'
import { userResponseDTO } from '../_data/user-dto'
import ExportData from './export-data'
import { ImportDataDialog } from './import-data-dialog'

interface ICouting {
  active: number
  deactivated: number
  invited: number
}

export default function UserTable() {
  const { organizationId, availableSeats } = useUser()

  const [counting, setCounting] = useState<ICouting>({
    active: 0,
    deactivated: 0,
    invited: 0,
  })

  const [{ pageSize, pageIndex }, setPagination] = useQueryStates(
    {
      // Use variable names that make sense in your codebase
      pageSize: parseAsInteger.withDefault(25),
      pageIndex: parseAsInteger.withDefault(0),
    },
    {
      urlKeys: {
        pageSize: 'limit',
        pageIndex: 'offset',
      },
    }
  )

  const [activeTab, setActiveTab] = useQueryState('status', {
    defaultValue: 'all',
  })

  const [search, setSearch] = useQueryState('search')
  const [{ sortBy, sortOrder }, setSorting] = useQueryStates(
    {
      // Use variable names that make sense in your codebase
      sortBy: parseAsString.withDefault('created_at'),
      sortOrder: parseAsString.withDefault('desc'),
    },
    {
      urlKeys: {
        sortBy: 'sortBy',
        sortOrder: 'sortOrder',
      },
    }
  )

  const query = {
    sort_by: sortBy,
    sort_order: sortOrder,
    filter_status:
      activeTab === 'active'
        ? '2'
        : activeTab === 'invited'
          ? '1'
          : activeTab === 'all'
            ? undefined
            : '0',
    search: search || undefined,
    limit: pageSize < 25 ? 25 : pageSize,
    offset: pageIndex * pageSize,
  }

  const { data, isLoading } = useQuery({
    ...getOrganizationUsersOrganizationOrganizationIdUsersGetOptions({
      path: {
        organization_id: organizationId,
      },
      query: query,
    }),
    select: userResponseDTO,
  })

  useEffect(() => {
    if (data) {
      setCounting({
        active: data?.activatedTotal ?? 0,
        deactivated: data?.deactivatedTotal ?? 0,
        invited: data?.pendingTotal ?? 0,
      })
    }
  }, [data])

  const { table, setPagination: tablePagination } = useTable({
    data: data?.items ?? [],
    columns: UserTableColumn,
    pageCount: calculatingPageCount(data?.total ?? 0, pageSize),
    onTableChange: (state) => {
      setPagination({ ...state })
    },
  })

  useEffect(() => {
    tablePagination({ pageIndex: 0, pageSize: 25 })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search])

  return (
    <div className="w-full overflow-hidden">
      <div className=" flex-none w-full">
        <p className="text-sm font-semibold text-gray-500">
          Available Seats:{' '}
          <span
            className={cn({
              'text-destructive font-semibold': availableSeats === 0,
            })}
          >
            {availableSeats}
          </span>
          {availableSeats === 0 && (
            <p className="text-destructive font-normal pt-1.5">
              Your organization has used all of it's available seats. Contact
              your Hydra Score Account Executive to request more seats.
            </p>
          )}
        </p>
        <div className="flex gap-4 my-4 flex-wrap">
          <SearchAutocompleted value={search} onChange={setSearch} />
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 w-full md:w-fit">
            <UserTab
              countTabs={{
                active: counting.active,
                deactivated: counting.deactivated,
                invited: counting.invited,
              }}
              value={activeTab}
              onChange={setActiveTab}
            />
            <UserSorting
              value={`${sortBy}.${sortOrder}`}
              onChange={(value) => {
                const [sortBy, sortOrder] = value.split('.')
                setSorting({ sortBy, sortOrder })
              }}
            />
          </div>
          <div className="flex-1 flex items-center justify-start h-9 pr-1 gap-x-2 xl:justify-end">
            <ImportDataDialog />
            <ExportData
              search={search}
              sortOrder={sortOrder}
              activeTab={activeTab}
              sortBy={sortBy}
              organizationId={organizationId}
            />
          </div>
        </div>
      </div>

      <Table
        table={table}
        isLoading={isLoading}
        totalRecords={data?.total}
        emptyMessage="No users found. Try adding new users or adjusting your filters."
      />
    </div>
  )
}
